import React, { useState } from 'react';

interface ThinkingStepsProps {
  /** Backend progress messages in the order they arrived. */
  steps: string[];
  /** True while the analysis is still running (spinner + live latest step). */
  running: boolean;
  /** Wall-clock seconds the run took; shown in the collapsed header once done. */
  durationSec?: number;
}

const formatDuration = (s?: number) => {
  if (typeof s !== 'number' || !isFinite(s) || s <= 0) return '';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return r ? `${m}m ${r}s` : `${m}m`;
};

/**
 * ChatGPT / Claude style "reasoning" disclosure.
 *
 * Collapsed by default: one line that shows the *latest* step while running
 * ("Attempt 2: widening cloud threshold…") or a summary once finished
 * ("Worked for 42s · 7 steps"). Clicking the row expands the full list.
 * The header is a single ellipsised line so a long backend message can never
 * push the bubble wider than the chat column (the mobile overflow bug).
 */
const ThinkingSteps: React.FC<ThinkingStepsProps> = ({ steps, running, durationSec }) => {
  const [open, setOpen] = useState(false);
  const latest = steps[steps.length - 1] || 'Working…';
  const dur = formatDuration(durationSec);
  const count = steps.length;

  const headline = running
    ? latest
    : `Worked${dur ? ` for ${dur}` : ''}`;

  return (
    <div className={`think ${running ? 'think-running' : 'think-done'} ${open ? 'think-open' : ''}`}>
      <button
        type="button"
        className="think-toggle"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        title={open ? 'Hide steps' : 'Show steps'}
      >
        <span className="think-icon" aria-hidden="true">
          {running ? <span className="spinner spinner-small"></span> : '✓'}
        </span>
        <span className="think-label">{headline}</span>
        {count > 0 && (
          <span className="think-count">{count} step{count === 1 ? '' : 's'}</span>
        )}
        <span className="think-chev" aria-hidden="true">▾</span>
      </button>

      {open && (
        <ol className="think-steps">
          {steps.map((s, i) => (
            <li
              key={`${i}-${s}`}
              className={running && i === steps.length - 1 ? 'think-step-current' : ''}
            >
              {s}
            </li>
          ))}
          {count === 0 && <li className="think-step-current">Working…</li>}
        </ol>
      )}
    </div>
  );
};

export default ThinkingSteps;
